import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import { FiBook, FiFileText, FiEye, FiDownload } from 'react-icons/fi';
import styles from './LessonModal.module.css';

const DASH = <span className={styles.dash}>—</span>;
const EMPTY = <div className={styles.muted}>Данные отсутствуют</div>;

const hasText = (v) => v != null && String(v).trim() !== '';
const text = (v) => (hasText(v) ? String(v) : null);

// Значение поля: прочерк, если пусто; в рамке со своей прокруткой для объёмных текстов
const Field = ({ label, value, scroll }) => (
    <div className={styles.fld}>
        <label>{label}</label>
        {hasText(value) ? (
            <div className={`${styles.val} ${scroll ? styles.scroll : ''}`}>{value}</div>
        ) : (
            <div className={styles.val}>{DASH}</div>
        )}
    </div>
);

const LitList = ({ items }) =>
    items && items.length ? (
        <ul className={styles.lst}>
            {items.map((it, i) => (
                <li key={it.id != null ? `${it.id}-${i}` : i}>
                    <span className={styles.ic}>
                        <FiBook size={14} />
                    </span>
                    <span>
                        {it.name}
                        {hasText(it.note) ? <span className={styles.note}> — {it.note}</span> : null}
                    </span>
                </li>
            ))}
        </ul>
    ) : (
        EMPTY
    );

const LitGroup = ({ title, group }) => {
    const g = group || {};
    const isText = hasText(g.text) && !(g.rpd && g.rpd.length) && !(g.ppz && g.ppz.length);
    return (
        <div className={styles.blk}>
            <div className={styles.grpTitle}>
                {title}
                {g.print_in_tp ? <span className={styles.mark}>вкл. в печать ТП</span> : null}
            </div>
            {isText ? (
                <div className={styles.val} style={{ whiteSpace: 'pre-line' }}>
                    {g.text}
                </div>
            ) : (
                <>
                    <div className={styles.subTitle}>Из рабочей программы дисциплины</div>
                    <LitList items={g.rpd} />
                    <div className={styles.subTitle}>Для освоения плана проведения занятия</div>
                    <LitList items={g.ppz} />
                </>
            )}
        </div>
    );
};

// строки плановой таблицы: часть + её подразделы (children)
const PlanRows = ({ items }) =>
    (items || []).flatMap((part, i) => {
        const rows = [
            <tr key={`p-${part.id ?? i}`} className={styles.lvl1}>
                <td>{text(part.name) || DASH}</td>
                <td className={styles.tm}>{part.time != null ? part.time : DASH}</td>
                <td>{text(part.content) || DASH}</td>
                <td>{text(part.equipment) || DASH}</td>
            </tr>,
        ];
        (part.children || []).forEach((sub, j) => {
            rows.push(
                <tr key={`p-${part.id ?? i}-s-${sub.id ?? j}`} className={styles.sub}>
                    <td>{text(sub.name) || DASH}</td>
                    <td className={styles.tm}>{sub.time != null ? sub.time : DASH}</td>
                    <td>{text(sub.content) || DASH}</td>
                    <td>{text(sub.equipment) || DASH}</td>
                </tr>
            );
        });
        return rows;
    });

const LessonModal = ({ lesson, isLoading, error, onClose, onOpenFile }) => {
    const [tab, setTab] = useState('main');

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    const stop = useCallback((e) => e.stopPropagation(), []);

    const general = lesson?.general || {};
    const main = lesson?.main || {};
    const planTable = lesson?.plan_table || {};
    const literature = lesson?.literature || {};
    const equipment = lesson?.equipment || {};
    const homework = lesson?.homework || {};
    const files = lesson?.files || {};
    const history = lesson?.lessons_history || [];
    const rec = lesson?.recommendations || {};
    const changed = lesson?.changed || null;

    const sections = [
        {
            key: 'main',
            title: 'Главная',
            has:
                hasText(general.tema) ||
                hasText(main.questions) ||
                hasText(main.study_goals) ||
                hasText(main.education_goals) ||
                hasText(main.place),
        },
        { key: 'plan', title: 'Плановая таблица', has: (planTable.items || []).length > 0 },
        {
            key: 'lit',
            title: 'Список литературы ППЗ',
            has: ['main', 'additional', 'official'].some((k) => {
                const g = literature[k] || {};
                return (g.rpd && g.rpd.length) || (g.ppz && g.ppz.length) || hasText(g.text);
            }),
        },
        {
            key: 'umo',
            title: 'Учебно-материальное обеспечение',
            has:
                hasText(equipment.visual_aids) ||
                hasText(equipment.audio) ||
                hasText(equipment.technical) ||
                hasText(equipment.assessment_tools),
        },
        {
            key: 'srs',
            title: 'Задание на самостоятельную работу',
            has: hasText(homework.text) || homework.hours != null || (homework.literature || []).length > 0,
        },
        {
            key: 'files',
            title: 'Файлы',
            has:
                (files.teacher || []).length > 0 ||
                (files.students || []).length > 0 ||
                (files.plan_table || []).length > 0,
        },
        { key: 'hold', title: 'Сведения о проведении', has: history.length > 0 },
        {
            key: 'omr',
            title: 'Организационно-методические рекомендации',
            has: hasText(rec.general) || hasText(rec.students) || hasText(rec.teacher),
        },
    ];

    const renderFilesGroup = (title, items) => (
        <div className={styles.blk}>
            <div className={styles.grpTitle}>{title}</div>
            {items && items.length ? (
                items.map((f, i) => (
                    <div className={styles.fRow} key={f.id != null ? `${f.id}-${i}` : i}>
                        <span className={styles.num}>{f.number != null ? f.number : i + 1}</span>
                        <span className={styles.ic}>
                            <FiFileText size={14} />
                        </span>
                        <div className={styles.fBody}>
                            <div className={styles.fName}>{f.name}</div>
                            <div className={styles.fDesc}>{hasText(f.description) ? f.description : '—'}</div>
                        </div>
                        <div className={styles.fAct}>
                            <button
                                type="button"
                                className={styles.fBtn}
                                disabled={!f.can_preview}
                                title={f.can_preview ? undefined : 'Файл можно только скачать'}
                                onClick={() => onOpenFile(f.id, { name: f.name, mode: 'view' })}
                            >
                                <FiEye size={14} /> Просмотреть
                            </button>
                            <button
                                type="button"
                                className={styles.fBtn}
                                onClick={() => onOpenFile(f.id, { name: f.name, mode: 'download' })}
                            >
                                <FiDownload size={14} /> Скачать
                            </button>
                        </div>
                    </div>
                ))
            ) : (
                EMPTY
            )}
        </div>
    );

    const renderTab = () => {
        switch (tab) {
            case 'main':
                return (
                    <>
                        <div className={styles.hInfo} style={{ marginBottom: 20 }}>
                            <div className={`${styles.f} ${styles.wide}`}>
                                <label>Тема занятия</label>
                                <div>{text(general.tema) || DASH}</div>
                            </div>
                            <div className={styles.f}>
                                <label>Номер по теме</label>
                                <div>{general.number_in_topic != null ? general.number_in_topic : DASH}</div>
                            </div>
                            <div className={styles.f}>
                                <label>Номер по виду занятия</label>
                                <div>
                                    {general.number_in_lesson_type != null ? general.number_in_lesson_type : DASH}
                                </div>
                            </div>
                            <div className={styles.f}>
                                <label>Рубежный контроль</label>
                                <div>{general.is_control ? 'Да' : 'Нет'}</div>
                            </div>
                        </div>
                        <Field label="Учебные вопросы" value={text(main.questions)} scroll />
                        <Field label="Учебные цели" value={text(main.study_goals)} scroll />
                        <Field label="Воспитательные цели" value={text(main.education_goals)} scroll />
                        <Field label="Место проведения" value={text(main.place)} />
                    </>
                );
            case 'plan':
                return (planTable.items || []).length ? (
                    <>
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Часть</th>
                                        <th className={styles.tm}>Время</th>
                                        <th>Содержание</th>
                                        <th>Наглядные пособия, АСО, ТСО</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <PlanRows items={planTable.items} />
                                    <tr className={styles.totalRow}>
                                        <td>ИТОГО</td>
                                        <td className={styles.tm}>
                                            {planTable.total_time != null ? planTable.total_time : ''}
                                        </td>
                                        <td />
                                        <td />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    EMPTY
                );
            case 'lit':
                return (
                    <>
                        <LitGroup title="Основная" group={literature.main} />
                        <LitGroup title="Дополнительная" group={literature.additional} />
                        <LitGroup title="Официальные издания" group={literature.official} />
                    </>
                );
            case 'umo':
                return (
                    <>
                        <Field label="Наглядные пособия" value={text(equipment.visual_aids)} scroll />
                        <Field
                            label="Аудиовизуальные средства обучения"
                            value={text(equipment.audio)}
                            scroll
                        />
                        <Field label="Технические средства обучения" value={text(equipment.technical)} scroll />
                        <Field label="Фонд оценочных средств" value={text(equipment.assessment_tools)} scroll />
                    </>
                );
            case 'srs':
                return (
                    <>
                        <Field label="Задание на самостоятельную работу" value={text(homework.text)} scroll />
                        <div className={styles.fld}>
                            <label>Литература к заданию</label>
                            <LitList items={homework.literature} />
                        </div>
                        <Field
                            label="Время на самостоятельную работу"
                            value={homework.hours != null ? `${homework.hours} ч` : null}
                        />
                    </>
                );
            case 'files':
                return (
                    <>
                        {renderFilesGroup('Файлы для преподавателей', files.teacher)}
                        {renderFilesGroup('Файлы для обучающихся', files.students)}
                        {renderFilesGroup('Файлы для плановой таблицы тематического плана', files.plan_table)}
                    </>
                );
            case 'hold':
                return history.length ? (
                    <>
                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Проведено</th>
                                        <th>Поток (группа)</th>
                                        <th>Дата занятия</th>
                                        <th>Время занятия</th>
                                        <th>Преподаватель</th>
                                        <th>Ср. балл</th>
                                        <th>Место проведения</th>
                                        <th>Продолжительность</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((r, i) => (
                                        <tr key={r.week_id != null ? `${r.week_id}-${r.gid}-${i}` : i}>
                                            <td>{r.passed ? 'Да' : 'Нет'}</td>
                                            <td>{text(r.class_name) || DASH}</td>
                                            <td>{text(r.lesson_date) || DASH}</td>
                                            <td>{text(r.lesson_time) || DASH}</td>
                                            <td>{text(r.teacher) || DASH}</td>
                                            <td>{r.avg_grade != null ? r.avg_grade : DASH}</td>
                                            <td>{text(r.room) || DASH}</td>
                                            <td>{r.duration != null ? r.duration : DASH}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className={styles.cntNote}>Всего записей: {history.length}</div>
                    </>
                ) : (
                    EMPTY
                );
            case 'omr':
                return (
                    <>
                        <Field
                            label="Организационно-методические рекомендации"
                            value={text(rec.general)}
                            scroll
                        />
                        <Field
                            label="Методические рекомендации обучающимся"
                            value={text(rec.students)}
                            scroll
                        />
                        <Field
                            label="Методические рекомендации преподавателю"
                            value={text(rec.teacher)}
                            scroll
                        />
                    </>
                );
            default:
                return null;
        }
    };

    const kindLabel = [general.lesson_type, general.lesson_type_lesson_number].filter(hasText).join(' ');
    const seqNumber = general.lesson_number != null ? general.lesson_number : general.lesson_type_lesson_number;

    const activeSection = sections.find((s) => s.key === tab);

    return createPortal(
        <div className={styles.mask} onClick={onClose}>
            <div className={styles.win} role="dialog" aria-modal="true" onClick={stop}>
                <div className={styles.head}>
                    <button type="button" className={styles.xBtn} onClick={onClose} aria-label="Закрыть">
                        ✕
                    </button>

                    {isLoading ? (
                        <div className={styles.loader}>
                            <Spinner animation="border" role="status">
                                <span className="sr-only">Loading...</span>
                            </Spinner>
                        </div>
                    ) : error ? (
                        <div className={styles.errorBox}>
                            <div className={styles.errorTitle}>Не удалось открыть занятие</div>
                            {error === 403
                                ? 'Нет доступа к тематическому плану занятия'
                                : error === 404
                                ? 'Занятие не найдено'
                                : 'Сервер вернул ошибку'}
                        </div>
                    ) : lesson ? (
                        <>
                            <div className={styles.kindRow}>
                                {hasText(kindLabel) ? <span className={styles.kind}>{kindLabel}</span> : null}
                                {general.is_control ? (
                                    <span className={styles.flag}>Рубежный контроль</span>
                                ) : null}
                            </div>
                            <h2 className={styles.headTitle}>{text(general.tema) || 'Занятие'}</h2>
                            <div className={styles.hInfo}>
                                <div className={`${styles.f} ${styles.wide}`}>
                                    <label>Номер и наименование раздела и темы</label>
                                    <div>{text(general.topic_title) || DASH}</div>
                                </div>
                                <div className={styles.f}>
                                    <label>Кафедра</label>
                                    <div>{text(general.cathedra) || DASH}</div>
                                </div>
                                <div className={styles.f}>
                                    <label>Семестр</label>
                                    <div>{text(general.term) || DASH}</div>
                                </div>
                                <div className={styles.f}>
                                    <label>Вид занятия</label>
                                    <div>{text(general.lesson_type) || DASH}</div>
                                </div>
                                <div className={styles.f}>
                                    <label>№ занятия (сквозной)</label>
                                    <div>{seqNumber != null && seqNumber !== '' ? seqNumber : DASH}</div>
                                </div>
                                <div className={styles.f}>
                                    <label>Длительность занятия (часов)</label>
                                    <div>{general.hours != null ? general.hours : DASH}</div>
                                </div>
                                <div className={styles.f}>
                                    <label>Кол-во часов по теме</label>
                                    <div>{general.topic_hours != null ? general.topic_hours : DASH}</div>
                                </div>
                                <div className={styles.f}>
                                    <label>Всего по виду и по теме</label>
                                    <div>
                                        {general.lesson_type_hours != null ? general.lesson_type_hours : DASH}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>

                {!isLoading && !error && lesson ? (
                    <>
                        <div className={styles.main}>
                            <nav className={styles.nav}>
                                {sections.map((s) => (
                                    <button
                                        key={s.key}
                                        type="button"
                                        className={s.key === tab ? styles.on : undefined}
                                        onClick={() => setTab(s.key)}
                                    >
                                        {s.title}
                                        {!s.has ? <span className={styles.no}>Данные отсутствуют</span> : null}
                                    </button>
                                ))}
                            </nav>
                            <div className={styles.body}>
                                <h3>{activeSection ? activeSection.title : ''}</h3>
                                {activeSection && !activeSection.has ? EMPTY : renderTab()}
                            </div>
                        </div>
                        <div className={styles.foot}>
                            <span className={styles.who}>
                                {changed && (hasText(changed.fio) || hasText(changed.at))
                                    ? `Изменил: ${[changed.fio, changed.at].filter(hasText).join(', ')}`
                                    : ''}
                            </span>
                            <button type="button" className={styles.closeBtn} onClick={onClose}>
                                Закрыть
                            </button>
                        </div>
                    </>
                ) : (
                    <div className={styles.foot}>
                        <span className={styles.who} />
                        <button type="button" className={styles.closeBtn} onClick={onClose}>
                            Закрыть
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default LessonModal;
